import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { supabase } from '@/src/lib/supabase/client';
import { IUIProfile } from '../types/IUIProfile';
import { editProfile } from '../actions/profile';
import { IProfile } from 'types/src/types';
import { identifyUser, resetUser } from '../lib/analytics/posthog';

export async function fetchProfile(): Promise<IUIProfile> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('ph_identified_user_id');
    resetUser();
    return {
      type: 'anonymous' as const,
      userId: '-1',
      fullName: '',
      avatarUrl: '',
      followers: 0,
      email: '',
      subscribers: 0,
      bits: 0,
      bio: '',
      uniqueLink: '',
      id: -1,
      isStripeConnected: false,
      countryCode: '',
      heardFrom: null,
      isPartner: false,
      role: null,
      isOnboarded: true,
      superLikesUsed: 0,
      level: 0,
      currentXP: 0,
      hasTier: false,
      tokens: 0,
      payment: { paypalStatus: null, provider: null, isEnabled: false as const },
    };
  }

  const last = localStorage.getItem('ph_identified_user_id');
  if (last !== user.id) {
    identifyUser({ id: user.id, email: user.email ?? null });
    localStorage.setItem('ph_identified_user_id', user.id);
  }

  const { data, error: profileError } = await supabase.functions.invoke(
    `user-profile?userId=${user.id}`,
    { method: 'GET' },
  );

  if (profileError || !data) {
    throw new Error(profileError?.message || 'Profile not found');
  }
  localStorage.setItem('isLoggedIn', 'true');

  const full =
    (typeof data.fullName === 'string' && data.fullName.trim()) ||
    `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();

  return {
    userId: user.id,
    fullName: full,
    avatarUrl: data.avatarUrl,
    followers: data.followers,
    subscribers: data.subscribers,
    bits: data.bits,
    bio: data.bio,
    email: data.email,
    uniqueLink: data.uniqueLink,
    id: data.id,
    type: 'registered' as const,
    isStripeConnected: data.isStripeConnected || false,
    countryCode: data.countryCode || '',
    isNewNotifications: data.isNewNotifications || false,
    heardFrom: data.heardFrom || null,
    isPartner: data.isPartner || false,
    role: data.role || null,
    isOnboarded: data.isOnboarded || false,
    superLikesUsed: data.superLikesUsed || 0,
    level: data.level || 0,
    currentXP: data.currentXP || 0,
    hasTier: data.hasTier || false,
    tokens: data.tokens || 0,
    payment: data.payment,
  };
}

export const useProfile = (): UseQueryResult<IUIProfile> => {
  return useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });
};

export function useProfileMutations() {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: { fullName?: string }) => editProfile(data),
    onSuccess: (result) => {
      if (result.data) {
        queryClient.setQueryData(['profile'], (old: IProfile | null) => ({
          ...old,
          fullName: result.data.fullName,
        }));
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
    },
  });

  return { updateMutation };
}

export function useSetOnboardedMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await editProfile({ isOnboarded: true } as any);
    },
    onSuccess: () => {
      qc.setQueryData(['profile'], (old: any) =>
        old ? { ...old, isOnboarded: true } : old,
      );
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
